import { restartableTask } from 'ember-concurrency';
// @ts-ignore
import ActionCell from 'volta/components/cell/action-cell';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
import PlanningOrder from 'volta/models/planning-order';
import SkuModel from 'volta/models/sku';
import { defaultErrorHandler, TError } from 'volta/utils/error-utils';
import { sum } from 'volta/utils/math-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type { IBvTableSort } from 'volta/components/bv-table';
import type { ISkuWorkshopCapacity } from 'volta/models/types/workshop';
import type StoreService from 'volta/services/-store';
import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type EventStreamService from 'volta/services/event-stream';
import { ICreateDraftOrderFromSku } from "volta/models/types/planning-order";

interface IArgs {
  skuId: string;
  unitMeasure?: string;
  close?: () => void;
  switchTab?: () => void;
  showOpenOrders?: (model: ISkuWorkshopCapacity) => void;
  showDetailsModal?: (workshopId: string) => void;
}

export default class SkuDetailsStock extends Component<IArgs> {
  // Services
  // ~~~~~

  @service store!: StoreService;
  @service intl!: IntlService;
  @service bvFlash!: BvFlashService;
  @service eventStream!: EventStreamService;
  @service bvMetrics!: BvMetricsService;

  // Properties
  // ~~~~~
  @tracked sku?: SkuModel;
  @tracked rows: ISkuWorkshopCapacity[] = [];
  @tracked sorts: IBvTableSort[] = [
    {
      valuePath: 'workshop.description',
      isAscending: true
    }
  ];
  @tracked selectedWorkshopId?: string;
  @tracked showStockDetails: boolean = false;

  // Callbacks
  // ~~~~~

  // Computed Properties
  // ~~~~~

  @sort('rows', 'sortProperties')
  sortedRows!: ISkuWorkshopCapacity[];

  get maxValue() {
    const maxStock = Math.max(...this.rows.map((r) => (r.onHand ?? 0) + (r.supply ?? 0)));
    const maxDemand = Math.abs(Math.min(...this.rows.mapBy('demand')));
    return Math.max(maxDemand, maxStock);
  }

  get totalOnHand() {
    return sum(this.rows.mapBy('onHand').toArray());
  }

  get totalDemand() {
    return sum(this.rows.mapBy('demand').toArray());
  }

  get totalSupply() {
    return sum(this.rows.mapBy('supply').toArray());
  }

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get totalRows() {
    return [
      {
        demand: this.totalDemand,
        supply: this.totalSupply,
        onHand: this.totalOnHand
      }
    ];
  }

  get columns() {
    const intl = this.intl;

    return [
      {
        columnName: intl.t('workshop'),
        valuePath: 'workshop.description',
        width: 200,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: (model: ISkuWorkshopCapacity) => {
          const ws = model.workshop?.description;
          const wsStockOut = model.workshopStockOut;
          return {
            label: ws,
            color: wsStockOut ? 'danger' : 'success'
          };
        }
      },

      {
        columnName: intl.t('demand'),
        valuePath: 'demand',
        width: 75,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        footerCellComponent: FormatCell,
        format: ({ demand }: ISkuWorkshopCapacity) => {
          return {
            value: demand,
            dataType: 'number'
          };
        }
      },
      {
        columnName: intl.t('supply'),
        valuePath: 'supply',
        width: 75,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        footerCellComponent: FormatCell,
        format: ({ supply }: ISkuWorkshopCapacity) => {
          return {
            value: supply,
            dataType: 'number'
          };
        }
      },
      {
        columnName: intl.t('onHand'),
        valuePath: 'onHand',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        footerCellComponent: FormatCell,
        format: ({ onHand, workshopStockOut }: ISkuWorkshopCapacity) => {
          return {
            value: onHand,
            dataType: 'number',
            cellClasses: workshopStockOut ? 'u-color-danger' : 'u-color-success'
          };
        }
      },
      {
        valuePath: 'draft',
        minWidth: 50,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        isSortable: false,
        cellComponent: ActionCell,
        format: (model: ISkuWorkshopCapacity) => {
          const reorderQty = this.calculateNeed(model.demand, model.supply, model.onHand);
          return {
            model,
            context: this,
            visible: reorderQty > 0,
            action: (m: ISkuWorkshopCapacity) => this.prepareOrder(m),
            icon: 'cart',
            text: intl.formatNumber(reorderQty),
            size: 'xs',
            tooltip: intl.t('planningsPage.actions.createDraftOrder', { count: 1 }),
            classNames: 'u-text-mono'
          };
        }
      },
      {
        valuePath: 'open-orders',
        width: 50,
        minWidth: 30,
        align: 'center',
        isReorderable: false,
        isResizable: false,
        isSortable: false,
        cellComponent: ActionCell,
        format: ({ workshop, supply, demand }: ISkuWorkshopCapacity) => {
          return {
            model: { workshopId: workshop?.workshopId },
            context: this,
            visible: workshop?.workshopId && (demand !== 0 || supply !== 0),
            action: (m: ISkuWorkshopCapacity) => this.args.showOpenOrders?.(m),
            iconRight: 'open',
            size: 'xs',
            tooltip: intl.t('showOpenOrders')
          };
        }
      },
      {
        columnName: '',
        valuePath: 'stock-locations',
        width: 50,
        minWidth: 30,
        align: 'center',
        isReorderable: false,
        isResizable: false,
        isSortable: false,
        cellComponent: ActionCell,
        format: ({ workshop }: ISkuWorkshopCapacity) => {
          return {
            model: workshop?.workshopId,
            context: this,
            visible: Boolean(workshop),
            action: (workshopId: string) => this.showStockDetailsModal(workshopId),
            iconRight: 'search',
            size: 'xs',
            tooltip: intl.t('showStockLocations')
          };
        }
      }
    ];
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    const es = this.eventStream;
    es.addHandler('plannings', 'PlanningsCalculationDone', this._onPlanningsCalculationDone, this);
    es.addHandler(
      'plannings',
      'PlanningsGlobalCalculationDone',
      this._onPlanningsCalculationDone,
      this
    );
  }

  @action
  handleDidInsert() {
    this.getWorkshopsCapacity.perform(this.args.skuId);
    this.getSku.perform(this.args.skuId);
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  // Actions
  // ~~~~~

  @action
  async prepareOrder(model: ISkuWorkshopCapacity) {
    this.bvMetrics.trackAction(this, 'prepareOrder');
    try {
      const payload = {
        skuId: model.sku.skuId,
        deliveryWorkshopId: model.workshop.workshopId,
        acqCode: 'DISTRIBUTE',
        qty: this.calculateNeed(model.demand, model.supply, model.onHand)
      } as ICreateDraftOrderFromSku;

      const updatedPO = await PlanningOrder.createDraftOrderFromSku(payload);
      return this._onCreateDraftOrderSuccess(updatedPO);
    } catch (e) {
      return this._onCreateDraftOrderError(e);
    }
  }

  @action
  showStockDetailsModal(workshopId: string) {
    this.bvMetrics.trackAction(this, 'showStockDetailsModal');
    this.selectedWorkshopId = workshopId;
    this.showStockDetails = true;
  }

  @action
  hideStockDetailsModal() {
    this.bvMetrics.trackAction(this, 'hideStockDetailsModal');
    this.selectedWorkshopId = undefined;
    this.showStockDetails = false;
  }

  // Helper Functions
  // ~~~~~

  calculateNeed(demand?: number, supply?: number, onHand?: number) {
    const need = (demand ?? 0) + (supply ?? 0) + (onHand ?? 0);
    return need >= 0 ? 0 : Math.abs(need);
  }

  // Private Callbacks/Handlers
  // ~~~~~

  getWorkshopsCapacity = restartableTask(async (skuId: string) => {
    try {
      const response: ISkuWorkshopCapacity[] = await this.store.customQuery(
        'sku',
        'workshopsCapacity',
        skuId,
        undefined
      );
      if (response && response.length) {
        this.rows = response.toArray();
      } else {
        this.rows = [];
      }
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  getSku = restartableTask(async (skuId: string) => {
    try {
      const response: SkuModel | undefined = await this.store.peekRecord('sku', skuId);
      this.sku = response;
      return this.sku;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  _onCreateDraftOrderSuccess(createdPO: PlanningOrder) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.bvFlash.success(this.intl.t('planningsPage.createDraftOrderSuccess'));
    return createdPO;
  }

  _onCreateDraftOrderError(reason: TError) {
    defaultErrorHandler(reason);
    this.bvFlash.apiError(reason);
  }

  _onPlanningsCalculationDone(payload: { skuIds: string[] } = { skuIds: [] }) {
    if (payload.skuIds.includes(this.args.skuId)) {
      this.getWorkshopsCapacity.perform(this.args.skuId);
    }
  }
}
